import { ChainId, PUBLIC_RPCS } from '@shared/utils/config/networks';
import { Connection } from '@solana/web3.js';
import { ethers } from 'ethers';
export default async function awaitTransactionConfirmation({
  hashPayload
}) {
  const controller = new AbortController();
  const networks = hashPayload === null || hashPayload === void 0 ? void 0 : hashPayload.map(x => x === null || x === void 0 ? void 0 : x.network);
  const TIMEOUT_MS = (networks === null || networks === void 0 ? void 0 : networks.includes(ChainId.ETHEREUM)) ? 45000 : 20000;
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const results = await Promise.all(hashPayload.map(async hash => {
      const abortSignal = controller.signal;
      if (hash.network === ChainId.SOLANA || hash.network === ChainId.SOLANA_DLN) {
        const connection = new Connection('https://mainnet.helius-rpc.com/?api-key=8bab07d6-d614-478e-ba36-c2e07ea6d720', 'confirmed');
        return confirmSolanaTransactionWithTimeout(connection, hash.hash, abortSignal);
      } else {
        const rpc = PUBLIC_RPCS[hash.network];
        return confirmEVMTransactionWithTimeout(rpc, hash.hash, abortSignal);
      }
    }));
    // If error or timeout return error
    const solanaTxnError = results.some(x => {
      return x === null || x === void 0 ? void 0 : x.error;
    });
    const evmTxnError = results.some(x => {
      var _a;
      return ((_a = x === null || x === void 0 ? void 0 : x.receipt) === null || _a === void 0 ? void 0 : _a.status) == 0;
    });
    console.log('Executing order in await finalization', results, solanaTxnError, evmTxnError);
    if (solanaTxnError || evmTxnError) {
      return {
        error: 'Transactions did not finalize! Please try again.'
      };
    }
    // Check if all transactions are Solana
    const allSolana = hashPayload.every(hash => hash.network === ChainId.SOLANA || hash.network === ChainId.SOLANA_DLN);
    return allSolana ? results : results;
  } catch (e) {
    if (e.name === 'AbortError') {
      return {
        error: 'Transactions did not finalize. Please try again.'
      };
    }
    console.error('Error in checkTransactionConfirmation', e);
    return {
      error: e.message
    };
  } finally {
    clearTimeout(timeoutId);
  }
}
async function confirmSolanaTransactionWithTimeout(connection, signature, abortSignal) {
  return new Promise((resolve, reject) => {
    let isResolved = false;
    let pollId = null;
    // Cleanup function to prevent further execution
    const cleanup = () => {
      isResolved = true;
      if (pollId) {
        clearTimeout(pollId);
        pollId = null;
      }
    };
    // Handle abort signal
    abortSignal.addEventListener('abort', () => {
      cleanup();
      reject(new Error('Transaction confirmation timed out'));
    }, {
      once: true
    });
    const checkSignature = async () => {
      var _a, _b, _c, _d;
      // Don't continue if already resolved or aborted
      if (isResolved || abortSignal.aborted) {
        cleanup();
        return;
      }
      try {
        const status = await connection.getSignatureStatus(signature);
        console.log('Executing order in confirmSolanaTransactionWithTimeout', status, signature);
        if ((_a = status === null || status === void 0 ? void 0 : status.value) === null || _a === void 0 ? void 0 : _a.err) {
          cleanup();
          resolve({
            status,
            signature,
            error: JSON.stringify(status.value.err)
          });
          return;
        }
        if (((_b = status === null || status === void 0 ? void 0 : status.value) === null || _b === void 0 ? void 0 : _b.confirmationStatus) === 'confirmed' || ((_c = status === null || status === void 0 ? void 0 : status.value) === null || _c === void 0 ? void 0 : _c.confirmationStatus) === 'finalized' || ((_d = status === null || status === void 0 ? void 0 : status.value) === null || _d === void 0 ? void 0 : _d.confirmationStatus) === 'processed') {
          cleanup();
          resolve({
            status,
            signature
          });
          return;
        }
        // Only schedule next check if not resolved and not aborted
        if (!isResolved && !abortSignal.aborted) {
          pollId = setTimeout(checkSignature, 500);
        }
      } catch (error) {
        cleanup();
        reject(error);
      }
    };
    console.log('Starting checkSignature', signature);
    checkSignature();
  });
}
async function confirmEVMTransactionWithTimeout(rpcUrl, txHash, abortSignal) {
  return new Promise((resolve, reject) => {
    const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
    const checkTransaction = async () => {
      try {
        const tx = await provider.getTransaction(txHash);
        if (!tx) {
          setTimeout(checkTransaction, 500);
          return;
        }
        const receipt = await tx.wait(1);
        if (receipt.status === 1) {
          resolve({
            receipt: receipt,
            txHash
          });
        } else {
          reject(new Error('Transaction failed'));
        }
      } catch (error) {
        reject(error);
      }
    };
    abortSignal.addEventListener('abort', () => {
      reject(new Error('Transaction confirmation timed out'));
    }, {
      once: true
    });
    checkTransaction();
  });
}